import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Search, UploadCloud, X } from 'lucide-react';
import { fetchMediaLibrary, uploadMediaFile } from '../lib/portalApi';

// Modal image browser: pick from the media library (activity images + uploads)
// or upload new files. `multiple` returns an array via onSelect, else a string.
export default function ImagePicker({ open, onClose, onSelect, multiple = false, title = 'Elegir imagen' }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const mediaQuery = useQuery({
    queryKey: ['admin', 'media-library'],
    queryFn: fetchMediaLibrary,
    enabled: open,
  });

  const uploadMutation = useMutation({
    mutationFn: async (files) => {
      const out = [];
      for (const file of files) out.push(await uploadMediaFile(file));
      return out;
    },
    onSuccess: async (results) => {
      setError('');
      await queryClient.invalidateQueries({ queryKey: ['admin', 'media-library'] });
      const urls = results.map((r) => r.url);
      if (multiple) setPicked((c) => [...c, ...urls]);
      else onSelect(urls[0]);
    },
    onError: (e) => setError(e.message || 'No se pudo subir la imagen.'),
  });

  if (!open) return null;

  const assets = mediaQuery.data?.items || [];
  const filtered = assets.filter((a) =>
    `${a.title} ${a.alt} ${a.category}`.toLowerCase().includes(search.trim().toLowerCase())
  );

  const toggle = (url) => {
    if (!multiple) { onSelect(url); return; }
    setPicked((c) => (c.includes(url) ? c.filter((u) => u !== url) : [...c, url]));
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex max-h-[88vh] w-full max-w-4xl flex-col rounded-[28px] bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-5">
          <h3 className="text-lg font-bold text-[#14213d]">{title}</h3>
          <div className="flex items-center gap-3">
            <label className="relative block w-56">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar"
                className="w-full rounded-full border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#1fa5a3]"
              />
            </label>
            <button type="button" onClick={onClose} className="text-slate-400 transition hover:text-slate-600">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="mb-5 flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:bg-[#f4fbfa]"
          >
            <UploadCloud className="h-5 w-5 text-[#1fa5a3]" />
            {uploadMutation.isPending ? 'Subiendo…' : 'Subir imagen desde mi equipo'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple={multiple}
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith('image/'));
              if (files.length) uploadMutation.mutate(files);
              e.target.value = '';
            }}
          />

          {error ? <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {filtered.map((a) => {
              const isPicked = picked.includes(a.url);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggle(a.url)}
                  className={`group relative overflow-hidden rounded-2xl border-2 text-left transition ${
                    isPicked ? 'border-[#1fa5a3]' : 'border-transparent hover:border-slate-300'
                  }`}
                >
                  <div className="aspect-[4/3] bg-slate-100">
                    <img src={a.thumb || a.url} alt={a.alt || a.title} loading="lazy" className="h-full w-full object-cover" />
                  </div>
                  {isPicked ? (
                    <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#1fa5a3] text-white">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                  ) : null}
                  <p className="truncate px-2 py-1.5 text-xs text-slate-600">{a.title}</p>
                </button>
              );
            })}
          </div>

          {!filtered.length && !mediaQuery.isLoading ? (
            <p className="py-10 text-center text-sm text-slate-500">No hay imágenes que coincidan.</p>
          ) : null}
        </div>

        {multiple ? (
          <div className="flex items-center justify-between gap-4 border-t border-slate-200 p-5">
            <p className="text-sm text-slate-600">{picked.length} seleccionadas</p>
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600">Cancelar</button>
              <button
                type="button"
                disabled={!picked.length}
                onClick={() => { onSelect(picked); setPicked([]); }}
                className="rounded-full bg-[#1fa5a3] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-50"
              >
                Añadir a la galería
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
